/**
 * Mobile burger accordion drawer — reference-faithful behavior.
 *
 * Open/close: data-drawer-open on .burger-drawer root drives all CSS transitions.
 * Accordion: jQuery slideDown/slideUp(300) — manages display+height atomically.
 * Scroll lock: body.header-drawer-open (defined in scss/sections/header.scss ~line 1457).
 *
 * ponytail: jQuery is global (theme.liquid:46) — no import needed.
 * ponytail: no push/pop stack — accordion replaces push model per Task 5 markup.
 */

import trapFocus from './header-focus-trap';

/** @returns {{ destroy(): void }} */
export default function initHeaderMobileDrawer() {
    const burgerDrawer = document.querySelector('.burger-drawer');
    const hamburger = document.querySelector('[data-header-hamburger]');

    // Guard: bail when markup is absent (desktop SSR may omit drawer).
    if (!burgerDrawer || !hamburger) return { destroy() {} };

    const closeBtn = document.getElementById('burger-drawer-close');
    const overlay = document.getElementById('BurgerDrawer-Overlay');

    // ── Listener registry (prevents double-bind on shopify:section:load) ────────
    const listeners = [];
    function on(el, type, fn) {
        if (!el) return;
        el.addEventListener(type, fn);
        listeners.push({ el, type, fn });
    }

    // ── Open / close ─────────────────────────────────────────────────────────────

    /** Active focus-trap release fn (null when closed). */
    let releaseTrap = null;

    function openDrawer() {
        burgerDrawer.setAttribute('data-drawer-open', '');
        document.body.classList.add('header-drawer-open');
        hamburger.setAttribute('aria-expanded', 'true');
        const dialogEl = burgerDrawer.querySelector('.drawer__inner') || burgerDrawer;
        releaseTrap = trapFocus(dialogEl); // Tab stays inside the dialog
        closeBtn?.focus();
    }

    function closeDrawer() {
        releaseTrap?.();
        releaseTrap = null;
        burgerDrawer.removeAttribute('data-drawer-open');
        document.body.classList.remove('header-drawer-open');
        hamburger.setAttribute('aria-expanded', 'false');
        hamburger.focus();
        closeAllSubpanels();
    }

    // ── Sub-panels (Figma 13852:19375) — parent rows push a second-level panel ──

    function setSubpanelTriggerExpanded(handle, expanded) {
        burgerDrawer
            .querySelectorAll(`[data-subpanel-trigger="${handle}"]`)
            .forEach((t) => t.setAttribute('aria-expanded', expanded ? 'true' : 'false'));
    }

    function closeAllSubpanels() {
        burgerDrawer.querySelectorAll('.drawer__subpanel[data-active]').forEach((p) => {
            p.removeAttribute('data-active');
            setSubpanelTriggerExpanded(p.getAttribute('data-subpanel'), false);
        });
    }

    function openSubpanel(handle, stack) {
        const panel = burgerDrawer.querySelector(`.drawer__subpanel[data-subpanel="${handle}"]`);
        if (!panel) return;
        // stack=true (trigger lives inside another panel): keep parent active so
        // back pops one level instead of jumping to the root list.
        if (!stack) closeAllSubpanels();
        panel.setAttribute('data-active', '');
        setSubpanelTriggerExpanded(handle, true);
        panel.scrollTop = 0;
        // move focus into the panel — back button is its first control.
        // preventScroll: the panel is mid-slide; a focus-reveal scroll would
        // shove the whole drawer shell sideways.
        panel.querySelector('[data-subpanel-back]')?.focus({ preventScroll: true });
    }

    // ── Accordion ─────────────────────────────────────────────────────────────────
    // Each .drawer__accordion-title is a button whose next sibling is .drawer__accordion-content.
    // Close any open same-level siblings first, then toggle the tapped one.

    function onAccordionClick(e) {
        const btn = e.currentTarget;
        const $btn = $(btn);
        const $content = $btn.next('.drawer__accordion-content');

        if (!$content.length) return;

        const isOpen = btn.getAttribute('aria-expanded') === 'true';

        // Close same-level open siblings first (reference pattern)
        // ponytail: parent().siblings() works for all 3 wrapper types:
        //   L1: div.burger_menu_items  L2-snippet: div.medium-font  L2-clinics: div.burger_menu_items
        $btn
            .parent()
            .siblings()
            .find('.drawer__accordion-title[aria-expanded="true"]')
            .each(function () {
                $(this).attr('aria-expanded', 'false');
                $(this).next('.drawer__accordion-content').stop(true, true).slideUp(300);
            });

        if (isOpen) {
            btn.setAttribute('aria-expanded', 'false');
            $content.stop(true, true).slideUp(300);
        } else {
            btn.setAttribute('aria-expanded', 'true');
            $content.stop(true, true).slideDown(300);
        }
    }

    // ── Drawer cart — mirror #cart-icon-bubble (Rebuy Smart Cart contract) ──────
    // The real cart icon is the single #cart-icon-bubble owned by the header bar
    // (rebuy-cart-sync/cart-drawer-shim bind to it). The drawer-top cart mirrors
    // its loading state and delegates clicks to it so Smart Cart opens.
    const cartBubble = document.getElementById('cart-icon-bubble');
    const drawerCart = burgerDrawer.querySelector('.drawer__cart');
    let cartObserver = null;

    if (cartBubble && drawerCart) {
        const LOADING = 'cart-icon--smart-cart-loading';
        const syncCartLoading = () => {
            drawerCart.classList.toggle('drawer__cart--loading', cartBubble.classList.contains(LOADING));
        };
        syncCartLoading();
        cartObserver = new MutationObserver(syncCartLoading);
        cartObserver.observe(cartBubble, { attributes: true, attributeFilter: ['class'] });

        on(drawerCart, 'click', (e) => {
            // Rebuy provider: bubble is role=button — delegate so Smart Cart opens
            if (cartBubble.getAttribute('role') === 'button') {
                e.preventDefault();
                if (cartBubble.classList.contains(LOADING)) return;
                closeDrawer();
                cartBubble.click();
            }
            // else: plain /cart link navigation — let it through
        });
    }

    // ── Keyboard (Escape closes drawer) ──────────────────────────────────────────

    function onKeydown(e) {
        if (e.key === 'Escape' && burgerDrawer.hasAttribute('data-drawer-open')) {
            e.preventDefault();
            // Escape pops the topmost sub-panel first; final press closes the drawer
            const active = burgerDrawer.querySelectorAll('.drawer__subpanel[data-active]');
            if (active.length) {
                const top = active[active.length - 1];
                top.removeAttribute('data-active');
                setSubpanelTriggerExpanded(top.getAttribute('data-subpanel'), false);
                // refocus the row that opened it
                burgerDrawer
                    .querySelector(`[data-subpanel-trigger="${top.getAttribute('data-subpanel')}"]`)
                    ?.focus();
            } else {
                closeDrawer();
            }
        }
    }

    // ── Bind ──────────────────────────────────────────────────────────────────────

    on(hamburger, 'click', openDrawer);
    on(closeBtn, 'click', closeDrawer);
    on(overlay, 'click', closeDrawer);
    // the fixed panel wrapper paints over the overlay — a click that lands on
    // the wrapper (i.e. the dimmed area, not the dialog) also closes
    on(document.getElementById('BurgerDrawer'), 'click', (e) => {
        if (e.target === e.currentTarget) closeDrawer();
    });
    on(document, 'keydown', onKeydown);

    // Accordion titles — use event delegation on the drawer root for re-init safety
    function onDrawerClick(e) {
        // Sub-panel push (parent rows) — takes precedence over accordion toggle
        const trigger = e.target.closest('[data-subpanel-trigger]');
        if (trigger) {
            const stack = !!trigger.closest('.drawer__subpanel');
            openSubpanel(trigger.getAttribute('data-subpanel-trigger'), stack);
            return;
        }
        const back = e.target.closest('[data-subpanel-back]');
        if (back) {
            // Pop only the panel this back button lives in (nested panels stack)
            const panel = back.closest('.drawer__subpanel');
            if (panel) {
                panel.removeAttribute('data-active');
                const handle = panel.getAttribute('data-subpanel');
                setSubpanelTriggerExpanded(handle, false);
                burgerDrawer.querySelector(`[data-subpanel-trigger="${handle}"]`)?.focus();
            }
            return;
        }
        const btn = e.target.closest('.drawer__accordion-title');
        if (btn) {
            // Simulate currentTarget by delegating to handler with btn as context
            const synth = { currentTarget: btn };
            onAccordionClick(synth);
        }
    }
    on(burgerDrawer, 'click', onDrawerClick);

    // ── destroy() ─────────────────────────────────────────────────────────────────

    function destroy() {
        listeners.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
        cartObserver?.disconnect();
        releaseTrap?.();
        releaseTrap = null;

        // Clean up open state
        burgerDrawer.removeAttribute('data-drawer-open');
        document.body.classList.remove('header-drawer-open');
        hamburger.setAttribute('aria-expanded', 'false');

        // Collapse all open accordions
        $(burgerDrawer).find('.drawer__accordion-content').stop(true, true).hide();
        $(burgerDrawer).find('.drawer__accordion-title').attr('aria-expanded', 'false');
    }

    return { destroy };
}
